<script setup lang="ts">
import { computed } from "vue";
import { ClockIcon } from "@heroicons/vue/24/outline";
import type { JobDetail } from "../composables/useJobs";
import { formatEta } from "../utils/format";

/**
 * Renders the in-flight panel for a job. Three sub-states inside
 * status='processing' (worker-progress-eta-one-pager.md):
 *
 *   - queued    → RunPod hasn't picked up the job yet
 *   - booting   → worker assigned but no progress emitted yet
 *   - active    → worker is emitting snapshots
 *
 * Strict C35 leak boundary: the rendered output NEVER names stages,
 * steps, the pipeline architecture, or infrastructure. Only "Waiting
 * in queue", "Starting", "Processing", a hedge ETA, the percentage,
 * the bar, and elapsed time appear.
 */
const props = defineProps<{
  job: JobDetail;
  /**
   * Render-time `now` in ms. The parent (JobDetailView) advances this
   * via its `nowMs` reactive so the elapsed counter ticks smoothly
   * between server polls. Centralising the clock in the parent keeps
   * a single time source and avoids per-component setInterval churn.
   */
  nowMs: number;
}>();

/**
 * Same tz-coercion helper JobDetailView uses; duplicated here to keep
 * the component self-contained and to avoid a circular import.
 * Naive ISO strings from segment_api (when datetime.utcnow() is used
 * Python-side) get a `Z` appended so Date.parse treats them as UTC.
 */
function parseUtcIso(iso: string): number {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return Date.parse(hasOffset ? iso : iso + "Z");
}

const phase = computed<"queued" | "booting" | "active">(() => {
  if (props.job.runpod_state === "queued") return "queued";
  if (props.job.progress?.overall_percentage != null) return "active";
  // Default branch covers two cases the spec collapses to "booting":
  //   - runpod_state === "active" but progress is null (true booting)
  //   - runpod_state is null / unknown — older API revision, first
  //     poll, or a refactor we haven't caught up to. Render as
  //     booting rather than nothing.
  return "booting";
});

// Anchor elapsed on the current attempt's start, NOT created_at. For
// a retried job, created_at is the original submission (potentially
// days ago) while the current attempt only began on retry — using
// created_at made the ETA project to >200h in production. See the
// elapsed-anchor section of retry-failed-jobs-one-pager.md. The
// fallback to created_at exists for legacy server responses during
// the rollout window (segment_api's column ships nullable on the wire).
const elapsedMs = computed(() => {
  const anchorIso = props.job.attempt_started_at ?? props.job.created_at;
  return props.nowMs - parseUtcIso(anchorIso);
});

function formatElapsedShort(ms: number): string {
  const secs = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
}

const elapsedLabel = computed(() => formatElapsedShort(elapsedMs.value));

const overallPercentage = computed(
  () => props.job.progress?.overall_percentage ?? null,
);

const etaLabel = computed(() =>
  formatEta(elapsedMs.value, overallPercentage.value),
);

/**
 * Bar width as an integer percentage, capped at 95 % per C32 so the
 * bar never visually claims "done" before the status flip.
 */
const barWidthPct = computed(() => {
  const pct = overallPercentage.value;
  if (pct == null) return 0;
  const capped = Math.max(0, Math.min(0.95, pct));
  return Math.round(capped * 100);
});

const headingLabel = computed(() => {
  if (phase.value === "queued") {
    return `Waiting in queue… (${elapsedLabel.value})`;
  }
  if (phase.value === "booting") {
    return `Starting… (${elapsedLabel.value})`;
  }
  // active
  return etaLabel.value != null
    ? `Processing — ${etaLabel.value}`
    : "Processing — estimating…";
});
</script>

<template>
  <div
    data-testid="status-processing"
    class="rounded-md border border-warning-border bg-warning-bg px-4 py-3 text-sm text-warning"
  >
    <p
      class="font-medium flex items-center gap-1.5"
      data-testid="progress-heading"
    >
      <ClockIcon class="h-4 w-4" aria-hidden="true" />
      {{ headingLabel }}
    </p>

    <div
      v-if="phase === 'active'"
      class="mt-2"
      data-testid="progress-active"
    >
      <div
        class="relative h-2 w-full overflow-hidden rounded bg-warning/20"
      >
        <div
          class="h-full bg-warning transition-[width] duration-300"
          :style="{ width: `${barWidthPct}%` }"
          data-testid="progress-bar"
          :aria-valuenow="barWidthPct"
          aria-valuemin="0"
          aria-valuemax="100"
          role="progressbar"
        />
      </div>
      <p class="mt-1 text-warning/80 flex items-baseline justify-between">
        <span data-testid="progress-percentage">{{ barWidthPct }}%</span>
        <span data-testid="progress-elapsed">Elapsed: {{ elapsedLabel }}</span>
      </p>
    </div>
  </div>
</template>
